import React, { useState, useCallback } from 'react';
import { Box, Typography, Button, TextField, Avatar, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import BlockEditor, { Block } from './BlockEditor';
import VideoSettings, { VideoContent } from './common/VideoSettings';
import QuizBuilder from './common/QuizBuilder';
import MediaLibrary from '../common/MediaLibrary';
import { QuizContent } from '../../types/course';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import QuizIcon from '@mui/icons-material/Quiz';
import { designTokens } from '../../theme/designTokens';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface ContentEditorProps {
    initialContent?: string; // Legacy support
    initialBlocks?: Block[];
    initialVideo?: VideoContent;
    initialQuiz?: QuizContent;
    initialExercise?: { statementBlocks: Block[]; correctionBlocks: Block[]; aiCorrectionEnabled: boolean };
    initialType?: 'text' | 'video' | 'quiz' | 'exercise';
    initialTitle?: string;
    onSave: (title: string, content: string, video: VideoContent | null, quiz: QuizContent | null, type: 'text' | 'video' | 'quiz' | 'exercise', blocks?: Block[], exercise?: { statementBlocks: Block[]; correctionBlocks: Block[]; aiCorrectionEnabled: boolean }) => void;
    onCancel: () => void;
}

const ContentEditor: React.FC<ContentEditorProps> = ({ initialContent = '', initialBlocks = [], initialVideo, initialQuiz, initialExercise, initialType = 'text', initialTitle = '', onSave, onCancel }) => {
    const [content, setContent] = useState(initialContent);
    const [blocks, setBlocks] = useState<Block[]>(initialBlocks.map(b => ({
        ...b,
        id: b.id || (b as any)._id || `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    })));

    // Exercise specific state
    const [statementBlocks, setStatementBlocks] = useState<Block[]>(initialExercise?.statementBlocks.map(b => ({
        ...b,
        id: b.id || (b as any)._id || `st-block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    })) || []);
    const [correctionBlocks, setCorrectionBlocks] = useState<Block[]>(initialExercise?.correctionBlocks.map(b => ({
        ...b,
        id: b.id || (b as any)._id || `cor-block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    })) || []);

    const [aiCorrectionEnabled, setAiCorrectionEnabled] = useState(initialExercise?.aiCorrectionEnabled || false);
    const [aiLoading, setAiLoading] = useState(false);
    const { showToast } = useToast();

    const handleUpdateBlocks = useCallback((updatedBlocks: Block[]) => {
        setBlocks(updatedBlocks);
    }, []);

    const handleUpdateStatementBlocks = useCallback((updatedBlocks: Block[]) => {
        setStatementBlocks(updatedBlocks);
    }, []);

    const handleUpdateCorrectionBlocks = useCallback((updatedBlocks: Block[]) => {
        setCorrectionBlocks(updatedBlocks);
    }, []);

    const [videoContent, setVideoContent] = useState<VideoContent>(initialVideo || { url: '', sourceType: 'url', player: 'youtube' });
    const [quizContent, setQuizContent] = useState<QuizContent>(initialQuiz || { questions: [] });
    const [type, setType] = useState<'text' | 'video' | 'quiz' | 'exercise'>(initialType);
    const [title, setTitle] = useState(initialTitle);
    const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);

    const handleAiCorrection = async () => {
        if (statementBlocks.length === 0) {
            showToast("Veuillez d'abord remplir l'énoncé.", "warning");
            return;
        }
        setAiLoading(true);
        try {
            const res = await api.post('/api/course/ai-exercise-correction', {
                statementBlocks
            });
            if (res.data.blocks) {
                setCorrectionBlocks([...correctionBlocks, ...res.data.blocks]);
                showToast("Correction suggérée avec succès", "success");
            }
        } catch (err: any) {
            console.error("AI Correction Error", err);
            showToast("Erreur lors de la génération de la correction", "error");
        } finally {
            setAiLoading(false);
        }
    };

    const handleSave = () => {
        onSave(
            title,
            content,
            type === 'video' ? videoContent : null,
            type === 'quiz' ? quizContent : null,
            type,
            type === 'text' ? blocks : undefined,
            type === 'exercise' ? { statementBlocks, correctionBlocks, aiCorrectionEnabled } : undefined
        );
    };

    const tabStyles = (active: boolean, activeColor: string) => ({
        borderRadius: '12px 12px 0 0',
        textTransform: 'none',
        fontWeight: 800,
        px: 4,
        py: 1.5,
        fontFamily: 'Quicksand, sans-serif',
        fontSize: '0.95rem',
        bgcolor: active ? activeColor : 'transparent',
        color: active ? '#fff' : '#718096',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
            bgcolor: active ? activeColor : 'rgba(0,0,0,0.03)',
            transform: !active ? 'translateY(-2px)' : 'none',
            '&:after': !active ? {
                width: '100%'
            } : {}
        },
        '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: active ? '100%' : '0%',
            height: '3px',
            bgcolor: active ? 'rgba(255,255,255,0.4)' : activeColor,
            transition: 'width 0.3s ease'
        }
    });

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f5f7fa' }}>
            {/* Header Tabs Only */}
            <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.05)', px: 4, pt: 1.5, pb: 0, boxShadow: '0 2px 15px rgba(0,0,0,0.02)', zIndex: 10 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={() => setType('text')}
                        sx={tabStyles(type === 'text', '#667eea')}
                    >
                        Constructeur mixte
                    </Button>
                    <Button
                        onClick={() => setType('video')}
                        sx={tabStyles(type === 'video', '#9c27b0')}
                    >
                        Vidéo unique
                    </Button>
                    <Button
                        onClick={() => setType('quiz')}
                        sx={tabStyles(type === 'quiz', '#10b981')}
                    >
                        Quiz d'évaluation
                    </Button>
                    <Button
                        onClick={() => setType('exercise')}
                        sx={tabStyles(type === 'exercise', '#ff8c00')}
                    >
                        Exercice (Énoncé + Correction)
                    </Button>
                </Box>
            </Box>

            {/* Content Area */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', p: { xs: 2, md: 4 } }}>
                {type === 'text' && (
                    <Box sx={{
                        height: '100%',
                        bgcolor: '#fff',
                        borderRadius: 4,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.03)'
                    }}>
                        <BlockEditor
                            initialBlocks={blocks}
                            onSave={handleUpdateBlocks}
                            onCancel={onCancel}
                        />
                    </Box>
                )}

                {type === 'video' && (
                    <Box sx={{ p: 5, bgcolor: '#fff', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', maxWidth: 850, mx: 'auto', mt: 2, border: '1px solid rgba(0,0,0,0.03)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Avatar sx={{ bgcolor: 'rgba(156,39,176,0.1)', color: '#9c27b0' }}>
                                <VideoLibraryIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d3748', fontFamily: 'Quicksand, sans-serif' }}>
                                    Configuration Vidéo
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#718096' }}>
                                    Mode simplifié pour une section contenant uniquement une vidéo.
                                </Typography>
                            </Box>
                        </Box>
                        <VideoSettings
                            content={videoContent}
                            onUpdate={setVideoContent}
                            setMediaLibraryOpen={() => setMediaLibraryOpen(true)}
                        />
                    </Box>
                )}

                {type === 'quiz' && (
                    <Box sx={{ p: 5, bgcolor: '#fff', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', maxWidth: 850, mx: 'auto', mt: 2, border: '1px solid rgba(0,0,0,0.03)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Avatar sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                                <QuizIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d3748', fontFamily: 'Quicksand, sans-serif' }}>
                                    Éditeur de Quiz
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#718096' }}>
                                    Créez des questions pour évaluer les connaissances de l'apprenant.
                                </Typography>
                            </Box>
                        </Box>
                        <QuizBuilder
                            content={quizContent}
                            onUpdate={setQuizContent}
                        />
                    </Box>
                )}

                {type === 'exercise' && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Logic Toggle */}
                        <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(0,0,0,0.03)', boxShadow: '0 5px 15px rgba(0,0,0,0.02)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}>
                                    <AutoAwesomeIcon />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>Correction IA pour l'apprenant</Typography>
                                    <Typography variant="body2" sx={{ color: '#718096' }}>Permet à l'apprenant de demander une correction de sa réponse en temps réel.</Typography>
                                </Box>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={aiCorrectionEnabled}
                                        onChange={(e) => setAiCorrectionEnabled(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={aiCorrectionEnabled ? "Activé" : "Désactivé"}
                                sx={{ mr: 2 }}
                            />
                        </Box>

                        <Box sx={{ flexGrow: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
                            {/* Enoncé */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.03)' }}>
                                <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,140,0,0.1)', color: '#ff8c00' }}>
                                        <SchoolIcon fontSize="small" />
                                    </Avatar>
                                    <Typography sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>Énoncé</Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                    <BlockEditor
                                        initialBlocks={statementBlocks}
                                        onSave={handleUpdateStatementBlocks}
                                        onCancel={onCancel}
                                    />
                                </Box>
                            </Box>

                            {/* Correction */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.03)' }}>
                                <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                                            <CheckCircleIcon fontSize="small" />
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>Correction Type (Modèle)</Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={aiLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                        disabled={aiLoading}
                                        onClick={handleAiCorrection}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}
                                    >
                                        Suggérer par l'IA
                                    </Button>
                                </Box>
                                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                    <BlockEditor
                                        initialBlocks={correctionBlocks}
                                        onSave={handleUpdateCorrectionBlocks}
                                        onCancel={onCancel}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            <MediaLibrary
                open={mediaLibraryOpen}
                onClose={() => setMediaLibraryOpen(false)}
                onSelect={(url: string) => {
                    setVideoContent({ ...videoContent, url, sourceType: 'library', player: 'native' });
                    setMediaLibraryOpen(false);
                }}
                mediaType={type === 'video' ? 'video' : 'all'}
            />

            {/* Floating Save Button */}
            <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{
                    position: 'fixed',
                    bottom: { xs: 20, md: 40 },
                    right: { xs: 20, md: 40 },
                    zIndex: 1100,
                    ...designTokens.actionButton,
                    px: 6,
                    py: 1.8,
                    borderRadius: 5,
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontFamily: 'Quicksand, sans-serif',
                    fontSize: '1rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: type === 'text' ? 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)' :
                        type === 'video' ? 'linear-gradient(45deg, #9c27b0 30%, #f06292 90%)' :
                            type === 'quiz' ? 'linear-gradient(45deg, #10b981 30%, #34d399 90%)' :
                                'linear-gradient(45deg, #ff8c00 30%, #ffae42 90%)',
                    '&:hover': {
                        transform: 'translateY(-5px) scale(1.05)',
                        boxShadow: '0 20px 45px rgba(0,0,0,0.3)',
                    }
                }}
            >
                Enregistrer la leçon
            </Button>
        </Box>
    );
};


export default ContentEditor;
